import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteSuborganizationById,
  deleteUserById,
  getAllPaises,
  getOrganizationById,
  getRolesByOrgId,
  getSuborganizationsByOrgId,
  getUsersByOrgId,
  postSuborganization,
  postUser,
  postUserInOrganization,
  postUsersInRole,
  putSuborganizationById,
  putUserById,
} from "../../api/broker";
import { iSingleOrganizationContextProps } from "../../interfaces/SuperAdmin/ContextProps/iSingleOrg";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { iDataRoles } from "../../interfaces/SuperAdmin/Pages/iDataRoles";
import {
  iDataSuborganizacion,
  PostSuborgFields,
} from "../../interfaces/SuperAdmin/Pages/iDataSuborganizacion";
import {
  iDataUser,
  PostUserFields,
  PutUserFields,
} from "../../interfaces/SuperAdmin/Pages/iDataUser";
import { iDataPaises } from "../../interfaces/SuperAdmin/Others/iDataPaises";

const SingleOrganizationContext =
  createContext<iSingleOrganizationContextProps>(
    {} as iSingleOrganizationContextProps
  );

interface Props {
  children: React.ReactNode;
}

const SingleOrganizationProvider: FC<Props> = ({ children }) => {
  const [tabValue, setTabValue] = useState<0 | 1>(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const [isLoadingSuborgs, setIsLoadingSuborgs] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isCreatingSuborg, setIsCreatingSuborg] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingSuborg, setIsEditingSuborg] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isDeletingSuborg, setIsDeletingSuborg] = useState(false);

  const [usuarios, setUsuarios] = useState<iDataUser[]>([]);
  const [organization, setOrganization] = useState<iDataOrganizacion>();
  const [suborgs, setSuborgs] = useState<iDataSuborganizacion[]>([]);
  const [roles, setRoles] = useState<iDataRoles[]>([]);
  const [paises, setPaises] = useState<iDataPaises[]>([]);

  const [selectedUser, setSelectedUser] = useState<iDataUser | null>(null);
  const [selectedSuborg, setSelectedSuborg] =
    useState<iDataSuborganizacion | null>(null);

  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isCreateSuborgModalOpen, setIsCreateSuborgModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isEditSuborgModalOpen, setIsEditSuborgModalOpen] = useState(false);
  const [toDeleteUser, setToDeleteUser] = useState("");
  const [toDeleteSuborg, setToDeleteSuborg] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(10); // Filas por página
  const [page, setPage] = useState(0); // Página actual
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [suborgSearchQuery, setSuborgSearchQuery] = useState("");

  const { idOrganization } = useParams();

  // Fetch de la organización al montar el componente
  useEffect(() => {
    const load = async () => {
      setIsLoadingOrg(true);
      try {
        const respUnitOrg = await getOrganizationById(idOrganization as string);
        if (respUnitOrg.data.statusCode === 200) {
          setOrganization(respUnitOrg.data.body);
        } else {
          toast.error("Error al obtener la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener la organización");
      } finally {
        setIsLoadingOrg(false);
      }
    };
    load();
  }, []);

  //Funcion para traer usuarios
  const fetchUsers = async () => {
    if (idOrganization) {
      setIsLoadingUsers(true);
      try {
        const userRes = await getUsersByOrgId(idOrganization);

        if (userRes.data.statusCode == 200) {
          console.log(userRes);
          setUsuarios(userRes.data.body);
        } else {
          toast.error("Error al obtener los usuarios de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener la info de la organización");
      } finally {
        setIsLoadingUsers(false);
      }
    }
  };

  //Funcion para traer suborganizaciones
  const fetchSuborgs = async () => {
    if (idOrganization) {
      setIsLoadingSuborgs(true);
      try {
        const res = await getSuborganizationsByOrgId(idOrganization);
        if (res.data.statusCode == 200) {
          console.log(res);
          setSuborgs(res.data.body);
        } else {
          toast.error("Error al obtener todas las suborganizaciones");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener todas las suborganizaciones");
      } finally {
        setIsLoadingSuborgs(false);
      }
    }
  };

  // Función para extraer roles
  const fetchRoles = async () => {
    if (idOrganization) {
      setIsLoadingRoles(true);
      try {
        const [rolesRes, paisRes] = await Promise.all([
          getRolesByOrgId(idOrganization),
          getAllPaises(),
        ]);

        if (rolesRes.data.statusCode === 200) {
          setRoles(rolesRes.data.body);
        } else {
          toast.error("Error al obtener los roles de la organización");
        }

        if (paisRes.data.statusCode === 200) {
          setPaises(paisRes.data.body);
        } else {
          toast.error("Error al obtener paises");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los roles de la organización y/o paises");
      } finally {
        setIsLoadingRoles(false);
      }
    }
  };

  // Sacamos los usuarios o suborganizaciones cuando cambiamos de tab
  useEffect(() => {
    if (organization) {
      if (tabValue === 0) {
        fetchUsers();
      } else {
        fetchSuborgs();
      }
    }
  }, [organization, tabValue]);

  const toggleCreateUserModal = (isOpen: boolean) => {
    if (isOpen) {
      fetchRoles();
    }
    setIsCreateUserModalOpen(isOpen);
  };

  const toggleCreateSuborgModal = (isOpen: boolean) => {
    setIsCreateSuborgModalOpen(isOpen);
  };

  const toggleEditUserModal = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedUser(null);
    }
    setIsEditUserModalOpen(isOpen);
  };

  const toggleEditSuborgModal = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedSuborg(null);
    }
    setIsEditSuborgModalOpen(isOpen);
  };

  const handleEditUser = (user: iDataUser) => {
    setSelectedUser(user);
    toggleEditUserModal(true);
  };

  const handleEditSuborg = (suborg: iDataSuborganizacion) => {
    setSelectedSuborg(suborg);
    toggleEditSuborgModal(true);
  };

  const userEdit = async (id_usuario: string, data: PutUserFields) => {
    setIsEditingUser(true);
    try {
      const res = await putUserById(id_usuario, data);
      console.log(res);
      if (res.data.statusCode === 200) {
        setUsuarios((prev) =>
          prev.map((user) => {
            if (user.id_usuario === id_usuario) {
              return res.data.body;
            }
            return user;
          })
        );
        toggleEditUserModal(false);
        toast.success("Usuario editado exitosamente");
      } else {
        toast.error("Error al editar usuario");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar usuario");
    } finally {
      setIsEditingUser(false);
    }
  };

  const suborgEdit = async (data: PostSuborgFields) => {
    if (selectedSuborg) {
      setIsEditingSuborg(true);
      try {
        const res = await putSuborganizationById(
          data as any,
          selectedSuborg.id_suborganizacion
        );

        if (res.data.statusCode === 200) {
          setSuborgs((prev) =>
            prev.map((item) => {
              if (
                item.id_suborganizacion === selectedSuborg.id_suborganizacion
              ) {
                return res.data.body;
              }
              return item;
            })
          );
          toggleEditSuborgModal(false);
          toast.success("Suborganización editada exitosamente");
        } else {
          toast.error("Error al editar suborganización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al editar suborganización");
      } finally {
        setIsEditingSuborg(false);
      }
    }
  };

  const userCreate = async (user: PostUserFields, roles: string[]) => {
    setIsCreatingUser(true);
    try {
      //Creamos el usuario
      const userRes = await postUser(user);
      const { statusCode, body } = userRes.data;
      console.log(userRes);

      if (statusCode === 200) {
        //Agregamos el usuario a la lista
        toast.success("Usuario registrado exitosamente en las DB");
        const rdsUser = body.rds;

        //Creamos la relación usuario-organizacion
        const orgRes = await postUserInOrganization(
          rdsUser.id_usuario,
          idOrganization as string
        );

        if (orgRes.data.statusCode === 201) {
          toast.success("Usuario agregado exitosamente a la organización");
        } else {
          toast.error("Error al agregar usuario a la organización");
        }

        // Creamos los roles
        const rolesRes = await Promise.all(
          roles.map((roleId) =>
            postUsersInRole({
              id_rol: roleId,
              id_usuarios: [rdsUser.id_usuario],
            })
          )
        );
        setUsuarios((prev) => [...prev, rdsUser]);
        console.log(rolesRes);

        const someResFailed = rolesRes.some(
          (role) => role.data.statusCode !== 201
        );

        if (someResFailed) {
          toast.error("Error al agregar el usuario a los roles");
        } else {
          toast.success("Usuario anexado exitosamente a los roles");
        }

        toggleCreateUserModal(false);
      } else {
        toast.error("Error al registrar el usuario en las DB");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al registrar el usuario en las DB");
    } finally {
      setIsCreatingUser(false);
    }
  };

  const suborgCreate = async (data: PostSuborgFields) => {
    setIsCreatingSuborg(true);
    try {
      const res = await postSuborganization(data);
      if (res.data.statusCode === 201) {
        setSuborgs((prev) => [...prev, res.data.body]);
        toggleCreateSuborgModal(false);
        toast.success("Suborganización creada exitosamente");
      } else {
        toast.error("Error al crear suborganización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar/crear suborganización");
    } finally {
      setIsCreatingSuborg(false);
    }
  };

  const assignToDeleteUser = (userId: string) => {
    setToDeleteUser(userId);
  };

  const assignToDeleteSuborg = (id: string) => {
    setToDeleteSuborg(id);
  };

  const userDelete = async () => {
    if (toDeleteUser) {
      setIsDeletingUser(true);
      try {
        const res = await deleteUserById(toDeleteUser);
        console.log(res);
        if (res.data.statusCode === 200) {
          const newArr = usuarios.filter(
            (user) => user.id_usuario !== toDeleteUser
          );
          setUsuarios(newArr);
          setToDeleteUser("");
          toast.success("Usuario borrado exitosamente");
        } else {
          toast.error("Error al borrar usuario");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al borrar usuario");
      } finally {
        setIsDeletingUser(false);
      }
    }
  };

  const suborgDelete = async () => {
    if (toDeleteSuborg) {
      setIsDeletingSuborg(true);
      try {
        const res = await deleteSuborganizationById(toDeleteSuborg);
        console.log(res);

        if (res.data.statusCode === 200) {
          setSuborgs((prev) =>
            prev.filter((item) => item.id_suborganizacion !== toDeleteSuborg)
          );
          setToDeleteSuborg("");
          toast.success("Suborganización eliminada exitosamente");
        } else {
          toast.error("Error al eliminar la suborganización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al eliminar la suborganización");
      } finally {
        setIsDeletingSuborg(false);
      }
    }
  };

  //Se ejecuta cuando el input busqueda cambia
  const handleUserSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSearchQuery(e.target.value);
    setPage(0); // Resetear a la primera página al buscar
  };

  //Se ejecuta al cambiar de pagna en el paginador
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  //Se ejecuta al cambiar la cantidad de filas por pagina en el paginador
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <SingleOrganizationContext.Provider
      value={{
        isLoadingUsers,
        isLoadingOrg,
        isLoadingRoles,
        isLoadingSuborgs,
        page,
        rowsPerPage,
        usuarios,
        suborgs,
        selectedUser,
        selectedSuborg,
        userSearchQuery,
        suborgSearchQuery,
        organization,
        tabValue,
        toDeleteUser,
        toDeleteSuborg,
        isCreateUserModalOpen,
        isCreateSuborgModalOpen,
        isEditUserModalOpen,
        isEditSuborgModalOpen,
        isCreatingUser,
        isCreatingSuborg,
        isEditingUser,
        isEditingSuborg,
        isDeletingUser,
        isDeletingSuborg,
        roles,
        paises,
        assignToDeleteUser,
        assignToDeleteSuborg,
        userDelete,
        suborgDelete,
        handleChangePage,
        handleChangeRowsPerPage,
        setTabValue,
        toggleCreateUserModal,
        toggleCreateSuborgModal,
        toggleEditUserModal,
        toggleEditSuborgModal,
        handleEditUser,
        handleEditSuborg,
        userEdit,
        suborgEdit,
        userCreate,
        suborgCreate,
        setUserSearchQuery,
        setSuborgSearchQuery,
      }}
    >
      {children}
    </SingleOrganizationContext.Provider>
  );
};

const useSuperAdminSingleOrg = () => useContext(SingleOrganizationContext);

export { SingleOrganizationProvider, useSuperAdminSingleOrg };
